/** @jsxImportSource @emotion/react */
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { Link } from 'react-router-dom'
import Text from 'src/components/ts-components/Text'
import GameListTeamLogo from '../../GameListTeamLogo'
import {
  teamContainerStyles,
  teamTextContainerStyles,
  captionSmallLabelStyles,
} from '../styles'

import { GameTeam } from '../types'

interface TeamSectionProps {
  team: GameTeam | undefined
  teamSlot?: GameTeam | undefined
  isHome: boolean
  scheduleId?: string | null
  /**
   * Admin-typed visitor name for a placeholder-visitor game, which has no
   * team and no slot. Resolved by the parent, which holds the game.
   */
  placeholderName?: string | null
}

const TeamSection = ({
  team,
  teamSlot,
  isHome,
  scheduleId,
  placeholderName,
}: TeamSectionProps) => {
  const to = team?.id
    ? `${ROUTES.TEAM_ROOT}/${team.id}${
        scheduleId ? `?sn_schedule=${scheduleId}` : ''
      }`
    : undefined
  const disableLink = !to || window.location.pathname === to

  /*
   * `??` throughout, not a truthiness ternary: the game parser sets
   * `visitingTeamSlot: {}` rather than leaving it undefined, so the old
   * `teamSlot ? …` branch was always taken and rendered `undefined` -- the
   * 'Visiting Team' fallback below was unreachable on a parsed game.
   *
   * The placeholder name sits between the slot and the generic label: a real
   * team wins, then a bracket slot, then the admin-typed name (SN-8410).
   */
  const teamName =
    team?.name_full ??
    teamSlot?.name_full ??
    placeholderName ??
    (isHome ? 'Home Team' : 'Visiting Team')

  const content = (
    <div css={teamContainerStyles(isHome)}>
      <GameListTeamLogo team={team} style={{ width: 60, height: 60 }} />
      <div css={teamTextContainerStyles(isHome)}>
        <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
          {teamName}
        </Text>
        <span css={captionSmallLabelStyles}>{isHome ? 'HOME' : 'VISITOR'}</span>
      </div>
    </div>
  )

  if (disableLink) {
    return content
  }

  return (
    <Link
      to={to!}
      onClick={(e) => e.stopPropagation()}
      style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}
    >
      {content}
    </Link>
  )
}

export default TeamSection
